import { useEffect, useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// Create context for language state
const LanguageContext = createContext(null);

// Detect device/browser language
function detectDeviceLanguage() {
  try {
    const deviceLang = (navigator.language || (navigator.languages && navigator.languages[0]) || 'en').toLowerCase();
    return deviceLang.startsWith('ar') ? 'ar' : 'en';
  } catch (e) {
    return 'en';
  }
}

export function useLanguage() {
  const { i18n } = useTranslation();
  const context = useContext(LanguageContext);

  // Normalize language code (ar-SA -> ar, en-US -> en)
  // Default to detected language if not set
  const normalizedLang = i18n.language?.startsWith('ar') ? 'ar' : 
                         i18n.language?.startsWith('en') ? 'en' : detectDeviceLanguage();
  const isRTL = normalizedLang === 'ar';

  useEffect(() => {
    document.documentElement.lang = normalizedLang;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    // Also set class for CSS targeting
    document.documentElement.classList.toggle('rtl', isRTL);
    document.documentElement.classList.toggle('ltr', !isRTL);
  }, [normalizedLang, isRTL]);

  const setLang = async (newLang) => {
    // Ensure we only use 'ar' or 'en', not regional variants
    const lang = newLang?.startsWith('ar') ? 'ar' : 'en';
    await i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.classList.toggle('rtl', lang === 'ar');
    document.documentElement.classList.toggle('ltr', lang !== 'ar');
  };

  const toggleLanguage = () => {
    const newLang = normalizedLang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
  };

  return {
    lang: normalizedLang,
    setLang,
    toggleLanguage,
    dir: isRTL ? 'rtl' : 'ltr',
    isRTL,
  };
}

export function LanguageProvider({ children }) {
  const [initialized, setInitialized] = useState(false);
  
  useEffect(() => {
    // On first load, detect language if no preference exists
    const savedLang = localStorage.getItem('lang');
    if (!savedLang) {
      const detectedLang = detectDeviceLanguage();
      localStorage.setItem('lang', detectedLang);
      document.documentElement.lang = detectedLang;
      document.documentElement.dir = detectedLang === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.classList.toggle('rtl', detectedLang === 'ar');
      document.documentElement.classList.toggle('ltr', detectedLang !== 'ar');
    }
    setInitialized(true);
  }, []);

  return (
    <LanguageContext.Provider value={{ initialized }}>
      {children}
    </LanguageContext.Provider>
  );
}
